import { useState, useEffect, useRef } from 'react';

import { IItem, IGroup, ISelectedItem } from '@/hooks/useKeyboardNavigation.types';

export function useKeyboardNavigation(
  group: IGroup,
  onSelect: (item: string, rowKey: string) => void,
  isEnabled: boolean,
) {
  const rowKeys = Object.keys(group);

  const initialRowIndex = rowKeys.findIndex((rowKey) => group[rowKey].some((item) => !item.isDisabled));
  const initialItemIndex = group[rowKeys[initialRowIndex]]?.findIndex((item) => !item.isDisabled) || 0;

  const [selectedItem, setSelectedItem] = useState<ISelectedItem>({
    rowIndex: initialRowIndex,
    itemIndex: initialItemIndex,
  });

  const ref = useRef<HTMLDivElement>(null);

  const findClosestEnabledItemIndex = (row: IItem[], startItemIndex: number): number => {
    let closestIndex = -1;
    let minDistance = Infinity;

    row.forEach((item, index) => {
      if (!item.isDisabled) {
        const distance = Math.abs(index - startItemIndex);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      }
    });

    return closestIndex;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isEnabled) {
        return;
      }

      const { key } = event;

      if (['Tab'].includes(key)) {
        event.preventDefault();
      }

      const { rowIndex, itemIndex } = selectedItem;
      const currentRow = group[rowKeys[rowIndex]];

      switch (key) {
        case 'Tab': {
          const newIndex = currentRow?.findIndex((item, index) => index > itemIndex && !item.isDisabled) || -1;
          if (newIndex !== -1) {
            setSelectedItem({ ...selectedItem, itemIndex: newIndex });
          } else {
            let newRowIndex = selectedItem.rowIndex < rowKeys.length - 1 ? selectedItem.rowIndex + 1 : 0;
            let newRow = group[rowKeys[newRowIndex]];
            while (newRow.every((item) => item.isDisabled)) {
              newRowIndex++;
              newRow =
                selectedItem.rowIndex < rowKeys.length - 1 ? group[rowKeys[newRowIndex]] : group[rowKeys[newRowIndex]];
            }
            setSelectedItem({ rowIndex: newRowIndex, itemIndex: findClosestEnabledItemIndex(newRow, 0) });
          }
          break;
        }
        case 'Enter': {
          const currentItem = group[rowKeys[rowIndex]][itemIndex];
          if (currentItem && onSelect) {
            onSelect(currentItem.value, rowKeys[rowIndex]);
          }
          break;
        }
        default:
          break;
      }
    };

    if (isEnabled) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEnabled, selectedItem, group, onSelect, rowKeys]);

  return {
    selectedItem,
    ref,
  };
}
